/** 
 * LMS Course view spot
 * Mockup: https://www.figma.com/proto/LlHPx5PfB6bTl2Yl43phMq/LXP
 * User story: https://dev.azure.com/gn-global-it/vs-cfa-digital-and-commercial-services-online/_workitems/edit/71608
 **/
import { utils } from "../../Foundation";
import { DatatableFooter } from "../../Foundation/datatable-footer";
import { Select } from "../select";
import { courseTemplate } from "./template";
import { preselectFiltersPassedInUrl } from './helpers/filters';

export class CourseView{
    constructor(element) {
        this._refs = {
            el: element,
            filtersElem: element.querySelector('.cards__filters'),
            content: element.querySelector('.cards__contents'),
            api_url: element.getAttribute('data-api-url'),
            filters: [],
            currentPage: 1,
            // Available data
            state: {
                totalItems: 0,
                data: [],
                backupData: [],
                filters: {}
            },
            settings: {
                maxPages: 5,
                pageSize: {
                    current: 10,
                    values: [10, 25, 50]
                },
                translations: {
                    footerInfo: 'Showing {startIndex} to {endIndex} of {total} results',
                    clearFilters: 'Clear all filters',
                    watch: 'Watch selection',
                    sorting: 'Sorting',
                    view: 'View',
                    noItems: 'No results were found with current filters, please try again',
                    searchPlaceholder: 'Search',
                    clear: 'Clear',
                    clearAll: 'Clear all',
                    tags: 'Tags',
                    training: 'Training'
                }
            },
            /***
             * Selected filter
             */
            queryString: {},
            eventList: {
                onChangePageSize: (e) => {
                    this._onChangePageSize(e);
                },
                onPageItemClick: (e) => {
                    this._onPageItemClick(e);
                },
                onArrowClick: (e) => {
                    this._onArrowClick(e);
                }
            }
        }

        this._init();
    }

    async _init() {
        // fetch data from api
        await this._fetchDataFromApi();
        // Create filters 
        this._createFilters();
        // Create sorting
        this._createSorting();
        // Create card items
        this._createCardItems();
        // Create footer
        this._createFooter();
        // Show selected filters
        this._filterCourses({ isInit: true });

        preselectFiltersPassedInUrl();
    }

    _createFilters() {
        // Create search field
        this._createInputField();
        for(const filter of this._refs.filters) {
            // Create dropdowns
            this._createDropDown(filter, this._refs.state.filters[filter]);
        }
    }

    _createInputField() {
        const oCol = utils.createElement('div', {
            'class': 'col-xs-12 col-md-12',
        }),
        oHtml = utils.createElement('div', {
            'class': 'input-group form-group'
        }, oCol),
        oInput = utils.createElement('input', {
            'class': 'form-control',
            'placeholder': this._refs.settings.translations.searchPlaceholder || ''
        }, oHtml),
        oIcon = utils.createElement('i', {
            'class': 'eva eva-search-outline',
            'aria-hidden': 'true'
        }, oHtml);
        this._refs.filtersElem.querySelector('.dropdown_filters').append(oCol);
        this._refs.queryString['input'] = [];

        // Event listener
        oInput.addEventListener('keyup', e => {
            this._refs.queryString.input = oInput.value;
            this._filterCourses({ isInit: false });
        })
    }

    _createDropDown(key, filterOptions, isMultiple = true) {
        const label = this._refs.settings.translations[key];
        // Build label + options
        let selectableFilters = [...filterOptions];
        // create dropdown
        const dropDownSettings = {
            'name': label,
            'innerHTML': `${selectableFilters.map(item => {
                const isSelected = this._refs.queryString[key].includes(item) ? 'selected=true' : '';
                return `<option value="${item}" name="${item}" ${isSelected}>${item}</option>`
            }).join("")}`,
        };
        if(isMultiple) {
            dropDownSettings.multiple = '';
        }
        const
            oCol = utils.createElement('div', {
                'class': 'col-xs-12 col-sm-6 col-md-4',
            }),
            oHtml = utils.createElement('div', {
                'class': 'form-select',
                'data-select': JSON.stringify({"placeholder": label}),
            }, oCol),
            oSelect = utils.createElement('select', dropDownSettings, oHtml);

        new Select(oHtml);
        this._refs.filtersElem.querySelector('.dropdown_filters').append(oCol);

        oSelect.addEventListener('change', e => {
            let contentFilters = [];
            for(const item of Array.from(e.target)) {
                if(item.selected) {
                    contentFilters.push(item.value);
                }
            }
            this._refs.currentPage = 1;
            this._setFilters(key, contentFilters);
            this._filterCourses({ isInit: false }); 
        })
    }

    _setFilters(key, value) {
        this._refs.queryString[key] = value;
    } 

    _createSorting() {
        // Create sort watch // If watch obj is in data (not in use)
        // this._toggleSortingButton(this._refs.settings.translations.watch, 'eye-outline');
        // Create toggle sort asc/desc
        this._refs.content.setAttribute('data-order', 'desc');
        this._toggleSortingButton(this._refs.settings.translations.sorting, 'bar-chart-2-outline', (e) => this._sortItemsAscDesc(e));
        // Create toggle grid/list view 
        const viewIcon = this._refs.el.querySelector('.cards__contents').className.includes('list-view') ? 'grid-outline' : 'list-outline';
        this._toggleSortingButton(this._refs.settings.translations.view, viewIcon, (e) => this._sortToggleGridList(e));
    }

    _sortItemsAscDesc(elem) {
        const toggleSort = () => {
            const currentOrder = this._refs.content.getAttribute('data-order') === 'asc' ? 'desc' : 'asc';
            elem.querySelector('i').classList.toggle('asc');
            this._refs.content.setAttribute('data-order', currentOrder);
            this._updateViewItems();
        };
        elem.addEventListener('keyup', (e) => {
            if(e.key === 'Enter') {
                toggleSort();
            }
        })
        elem.addEventListener('click', () => {
            toggleSort();
        });
    };

    _setSortOrder() {
        const order = this._refs.content.getAttribute('data-order');
        this._sort(order);
    };

    _sort(sortOrder) {
        this._refs.state.data.sort((a, b) => {
            // Fixed sort on the newest content
            const sortItemA = sortOrder === 'desc' ? a.date['iso'] : b.date['iso'];
            const sortItemB = sortOrder === 'desc' ? b.date['iso'] : a.date['iso'];
            if (sortItemB.toUpperCase() < sortItemA.toUpperCase()) {
                return -1;
            }
            if (sortItemB.toUpperCase() > sortItemA.toUpperCase()) {
                return 1;
            }
            return 0;
        });
    };

    _sortToggleGridList(elem) {
        const toggle = () => {
            this._refs.el.querySelector('.cards__contents').classList.toggle('list-view');
            elem.querySelector('i').classList.toggle('eva-grid-outline');
            elem.querySelector('i').classList.toggle('eva-list-outline');
            this._updateViewItems();
        };
        elem.addEventListener('keyup', (e) => {
            if(e.key === 'Enter') {
                toggle();
            }
        });
        elem.addEventListener('click', () => {
            toggle();
        });
    }

    _toggleSortingButton(label, icon, cb) {
        const
            oCon = utils.createElement('div', {
                'class': 'sort_container d-flex align-items-center',
                'tabindex': '0'
            }),
            oIcon = utils.createElement('i', {
                'class': `eva eva-${icon}`,
                'aria-hidden': 'true'
            }, oCon),
            oElem = utils.createElement('div', {
                'innerHTML': label
            }, oCon);

        this._refs.filtersElem.querySelector('.sort_elements').append(oCon);
        
        if(cb) cb(oCon);
    }

    async _createCardItems() {
        // Set sort order
        this._setSortOrder();
        // Generate list of items
        const filteredList = await this._filterListOfItems();
        let contentList = '';

        if(filteredList.length > 0) {
            await filteredList.forEach(item => {
                const cardItem = this._cardTemplate(item);
                contentList += cardItem;
            })
        } else {
            const noItem = this._noItemTemplate();
            contentList += noItem;
        }
        this._refs.content.innerHTML = contentList;

        // Add Event listener to open new window
        this._showVideoWindow();
    }

    _createFooter() {
        new DatatableFooter(this._refs);
    }

    async _filterListOfItems() {
        let startItem = 0;
        let endItem = this._refs.settings.pageSize.current * this._refs.currentPage;
        // Set item start position
        if (this._refs.currentPage > 1) {
            startItem = this._refs.settings.pageSize.current * (this._refs.currentPage - 1);
        }
        // Filter the list to display 10 / 25 / 50 items
        const filterList = await this._refs.state.data.filter(
            (data, i) => i < endItem && startItem <= i
        );
        return filterList;
    }

    async _fetchDataFromApi() {
        const apiUrl = this._refs.api_url;
        await fetch(apiUrl)
            .then((response) => response.json())
            .then((json) => {
                if(json) {
                    this._updateState(json);
                }
            }).catch( err => {
                console.error('Fetch data from API failed.');
                console.error('Error: ', err);
            });
    }

    // State management
    _updateState(input_data) {
        if(input_data.data) {
            this._refs.state.data = [...this._refs.state.data, ...input_data.data];
            this._refs.state.backupData = this._refs.state.data;
            this._refs.state.totalItems = this._refs.state.data.length;
        }
        if(input_data.settings && input_data.settings.translations) {
            this._refs.settings.translations = {...this._refs.settings.translations, ...input_data.settings.translations};
        }
        // Update filter dropdowns
        this._fetchFilters(input_data);
    }

    _fetchFilters(input_data) {
        const data = input_data.data;
        const preselectFilters = input_data.preselectFilters;
        const filter_list = data
        .map(item => item.filters) // Return item filters
        .filter(item => item) // Remove (if item filters) undefined
        .flat(); // Remove nested array

        // Fetch filter names
        const filter_names = filter_list.map(filter => filter.name);
        this._refs.filters = [...new Set(filter_names)]; // Add to global filter list + remove duplicates

        // Fetch filter option names
        this._refs.state.filters = this._fetchFiltersOptions(filter_list, false);

        // Fetch preselected filters
        if(typeof preselectFilters === 'object') {
            for(const filter of this._refs.filters) {
                this._refs.queryString[filter] = preselectFilters[filter];
            }
        }
    }

    _fetchFiltersOptions(filter_list) {
        return filter_list.reduce((acc, curr) => {
            if(!acc[curr.name]) acc[curr.name] = []; //If this type wasn't previously stored
            for(const option of curr.options) {
                acc[curr.name].push(option);
            }
            acc[curr.name] = [...new Set(acc[curr.name])]
            return acc;
        }, {});
    }

    _cardTemplate(settings) {
        const templateSettings = {...settings};
        
        // Check view template
        const isListView = this._refs.el.querySelector('.cards__contents').className.includes('list-view');
        // Apply progress
        templateSettings.progress = courseTemplate.generateProgress(this._refs, templateSettings, isListView);
        // Add optional elements
        if(templateSettings.bookmark) {
            templateSettings.bookmark.class = settings.bookmark.isMarked ? 'bookmark isMarked' : 'bookmark';
        }
        // Add svg stars
        if(templateSettings.rating) {
            templateSettings.stars = courseTemplate.generateStars(templateSettings);
        }
        // Generate template view
        if(isListView) {
            return courseTemplate.generateListView(templateSettings);
        }
        return courseTemplate.generateGridView(templateSettings);
    }

    _noItemTemplate() {
        return courseTemplate.generateNoItemView(this._refs.settings.translations.noItems);
    }

    /**
     * Event listeners 
     * **/
    _filterCourses({ isInit }) {
        const inputValue = this._refs.queryString.input;
        let selectedFilters = [];
        let filtersFromUrl = decodeURI(window.location.search.split("?filters=")[1]);

        if(isInit && filtersFromUrl !== 'undefined') {
            selectedFilters = [...filtersFromUrl.split(",")];
        } else {
            for(const filter of this._refs.filters) {
                selectedFilters = [...selectedFilters, ...this._refs.queryString[filter]];
            }
        }
        
        // Append filters tags
        if(selectedFilters.length > 0 && selectedFilters[0] !== 'none') {
            const filtersToURL = selectedFilters.join();
            window.history.pushState(null, null, `?filters=${filtersToURL}`);
            this._appendFilters(selectedFilters);
        } else {
            // Clear filters
            this._removeFilterContent();
            window.history.replaceState(null, null, `?filters=none`);
        }

        // Filter contents
        this._refs.state.data = this._refs.state.backupData
        .filter( course => {
            // Filter is not included => Hide course 
            if(!course.filters) return false;
            // Filter match dropdowns
            if(selectedFilters.length > 0) {
                const courseFilters = course.filters
                .map(item => item.options) // Map all options
                .flat(); // remove nested array

                if(courseFilters.length > 0) {
                    return selectedFilters.some(item => courseFilters.includes(item));
                }
            } else {
                return true;
            }
        })
        .filter( course => {
            // Filter match input field
            if(inputValue.length > 0) {
                const courseContent = `${course.content.headline.toLowerCase()} ${course.content.description.toLowerCase()}`;
                return courseContent.includes(inputValue.toLowerCase());
            } else {
                return true;
            }
        })
        
        this._refs.state.totalItems = this._refs.state.data.length;
        this._refs.settings.pageSize.current = this._refs.el.getAttribute('data-page-size') || this._refs.settings.pageSize.current;
        // Update content view
        this._updateViewItems();
        // Update footer
        this._refs.el.querySelector('.datatable__footer').remove();
        this._createFooter();
    }

    _appendFilters(selectedFilters) {
        const filters = [...selectedFilters];
        // Clear filters
        this._removeFilterContent();

        const selectedFilterField = this._refs.el.querySelector('.cards__selected_filters');
        const oList = utils.createElement('ul', {
            'class': 'product-filter__list',
        }, selectedFilterField);

        /**
         * Create clear all button
         */
        this.clearAllButton = courseTemplate.clearAllButton(selectedFilterField, this._refs.settings.translations.clearAll);
        if(this.clearAllButton) {
            this.clearAllButton.addEventListener('click', this._clearAllFilters.bind(this));
            this.clearAllButton.addEventListener('keyup', e => {
                if(e.key === 'Enter') {
                    this.clearAllButton.click();
                }
            })
        }

        /**
         * Create selected filter button
         */
        for(const filter of filters) {
            // Build selected filter label
            const elArray = courseTemplate.filterLabel(oList, filter, this._refs.settings.translations.clear);
            for(const el of elArray) {
                el.addEventListener('click', this._clearSelectedFilter.bind(this));
                el.addEventListener('keyup', e => {
                    if(e.key === 'Enter') {
                        listItem.click();
                    }
                })
            }
        }
    }

    /**
     * Remove filter content list
     */
    _removeFilterContent() {
        const selectedFilterlist = this._refs.el.querySelector('.cards__selected_filters .product-filter__list');
        if(selectedFilterlist) {
            selectedFilterlist.remove()
            this.clearAllButton.remove();
        };
    }

    /**
     * Remove all filters and render content view
     */
    _clearAllFilters() {
        this._removeFilterContent();
        
        // Remove all filters from state
        let allFilters = [];
        for(const filter of this._refs.filters) {
            allFilters = [...allFilters, ...this._refs.queryString[filter]];
            this._refs.queryString[filter] = [];
        }
        // Unchecked all filters from dropdown
        for(const filter of allFilters) {
            this._removeFilter(filter)
        }
        this.clearAllButton.remove();
        
        // Update content view
        this._filterCourses({ isInit: false });
    }

    /**
     * Remove selected filter 
     */
    _clearSelectedFilter(e) {
        this._removeFilterContent();

        // get selected value
        const filterValue = e.target.dataset.value;
        // Remove filter value from state
        for(const filter of this._refs.filters) {
            this._refs.queryString[filter] = this._refs.queryString[filter].filter(elem => elem != filterValue);
        }
        // unchecked filter value from dropdown
        this._removeFilter(filterValue);

        // Update content view
        this._filterCourses({ isInit: false });
    }

    /**
     * Unchecked dropboxes & select multiple options set to false
     */
    _removeFilter(value) {
        const checkbox = Array.from(this._refs.el.querySelectorAll('input[type="checkbox"]'));
        const option = Array.from(this._refs.el.querySelectorAll('select[multiple] option'));
        const allFields = [...checkbox, ...option];
        for(const field of allFields) {
            if(field.innerText === value || field.value === value) {
                if(field.selected) {
                    field.selected = false;
                }
                if(field.checked) {
                    field.checked = false;
                }
            }
        }
    }

    _updateViewItems() {
        // Clean contents
        this._refs.content.innerHTML = '';
        // Set item order
        this._setSortOrder();
        // Create card items
        this._createCardItems();
        // Update footer
        this._refs.el.setAttribute('data-currentPage', this._refs.currentPage);
    }

    _onChangePageSize(e) {
        this._refs.currentPage = 1;
        this._refs.settings.pageSize.current = Number(e.target.options[e.target.selectedIndex].value);
        this._updateViewItems();
    }

    _onPageItemClick(e) {
        this._refs.currentPage = parseInt(e.target.innerHTML);
        this._updateViewItems();
    }

    _onArrowClick(e) {
        // Direction
        if (e.target.classList.contains('is-left')) {
            this._refs.currentPage--;
        } else {
            this._refs.currentPage++;
        }
        this._updateViewItems();
    }

    _showVideoWindow() {
        const ctaButtons = this._refs.el.querySelectorAll('.card_show_video');
        const calcWindowSize = () => {
            /**
             * Hardcode Browser to 80% screen size
             * Browser position center of screen
             */
            const newBrowserSize = 0.8;
            const newBrowserPosition = (1 - newBrowserSize) / 2;
            let windowSize = '';
            let screenWidth = window.screen.width;
            let screenHeight = window.screen.height;
            let offsetX = 0;
            let offsetY = 0;
            /**
             * Max size
             */
            if(screenWidth > 1920) {
                screenWidth = 1920;
                screenHeight = 1080;
                // calc screen differences
                offsetX = (window.screen.width - screenWidth) / 2;
                offsetY = (window.screen.height - screenHeight) / 2;
            }
            
            const browserWidth = screenWidth * newBrowserSize;
            const browserHeight = screenHeight * newBrowserSize;
            const posleft = screenWidth * newBrowserPosition + offsetX;
            const posTop = screenHeight * newBrowserPosition + offsetY;
            if(browserWidth && browserHeight) {
                windowSize = `width=${browserWidth},height=${browserHeight},top=${posTop},left=${posleft}`;
            }
            return windowSize;
        }
        const openNewWindow = (elem) => {
            const url = elem.dataset.url;
            const windowSize = calcWindowSize();
            let settings = `
                directories=no,
                titlebar=no,
                toolbar=no,
                location=no,
                status=no,
                menubar=no,
                scrollbars=no,
                ${windowSize}
            `;

            window.open(url, 'winname', settings);
            // Refresh original window to get new session timeout
            window.location.reload(true);
        }
        for(const cta of ctaButtons) {
            cta.addEventListener('click', e => openNewWindow(cta));
            cta.addEventListener('keyup', e => {
                if(e.key === 'Enter') {
                    cta.click();
                }
            })
        }
    }
}